import { useEffect, useState, useCallback, useRef } from 'react';

interface UseEditableAutocompleteConfig<Option, Params> {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  useApiHook: () => {
    data: Option[] | undefined;
    loading: boolean;
    [key: string]: any;
  };
  getterName: string;
  defaultParams: Params;
  filterOptions?: (options: Option[]) => Option[];
  mapOptions?: (options: Option[]) => Option[];
  deduplicateOptions?: boolean;
  deduplicateKey?: keyof Option;
  additionalDependency?: any;
  skipFetchCondition?: () => boolean;
}

export function useEditableAutocomplete<Option extends { id: any }, Params>({
  value,
  onChange,
  onSave,
  useApiHook,
  getterName,
  defaultParams,
  filterOptions = (opts) => opts.filter((o: any) => o.enabled),
  mapOptions,
  deduplicateOptions = true,
  deduplicateKey = 'id' as keyof Option,
  additionalDependency,
  skipFetchCondition,
}: UseEditableAutocompleteConfig<Option, Params>) {
  const apiHook = useApiHook();
  const { data, loading } = apiHook;
  const getter = apiHook[getterName];

  const [options, seOptions] = useState<Option[]>([]);
  const [params, setParams] = useState<Params>(defaultParams);
  const hasUserInteractedRef = useRef(false);

  const filterOptionsRef = useRef(filterOptions);
  const mapOptionsRef = useRef(mapOptions);
  const skipFetchConditionRef = useRef(skipFetchCondition);

  filterOptionsRef.current = filterOptions;
  mapOptionsRef.current = mapOptions;
  skipFetchConditionRef.current = skipFetchCondition;

  const paramsRef = useRef<Params>(params);
  paramsRef.current = params;

  const optionsFilterTextRef = useRef<string>('__none__');

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  useEffect(() => {
    if (skipFetchConditionRef.current && skipFetchConditionRef.current()) {
      return;
    }

    if (typeof getter === 'function') {
      if (additionalDependency !== undefined) {
        const getterWithDep = getter(additionalDependency);
        if (typeof getterWithDep === 'function') {
          getterWithDep(params);
        }
      } else {
        getter(params);
      }
    }
  }, [getter, params, additionalDependency]);

  useEffect(() => {
    if (hasUserInteractedRef.current) {
      if (value?.code && !value?.id) {
        setParams(
          (prev) =>
            ({
              ...prev,
              filterText: value.code,
              pageNumber: 1,
            } as Params),
        );
      } else if (typeof value === 'string' && value.trim() !== '') {
        setParams(
          (prev) =>
            ({
              ...prev,
              filterText: value,
              pageNumber: 1,
            } as Params),
        );
      }
    }
  }, [value?.code, value?.id, value]);

  useEffect(() => {
    if (!value || value === null) {
      seOptions([]);
      optionsFilterTextRef.current = '__none__';
      setParams(defaultParams);
    }
  }, [value, defaultParams]);

  useEffect(() => {
    if (data?.length) {
      let processedOptions = mapOptionsRef.current
        ? mapOptionsRef.current(data)
        : data;
      processedOptions = filterOptionsRef.current(processedOptions);

      seOptions((prev) => {
        const currentFilter = (paramsRef.current as any).filterText ?? '';
        const isNewFilter = optionsFilterTextRef.current !== currentFilter;
        optionsFilterTextRef.current = currentFilter;

        if (isNewFilter || prev.length === 0) {
          return processedOptions;
        }

        if (!deduplicateOptions) {
          return [...prev, ...processedOptions];
        }

        const map = new Map(
          prev.map((option) => [option[deduplicateKey], option]),
        );
        processedOptions.forEach((option) => {
          map.set(option[deduplicateKey], option);
        });
        return Array.from(map.values());
      });
    }
  }, [data, deduplicateOptions, deduplicateKey]);

  const handleSearchChange = useCallback(
    (newValue: string) => {
      hasUserInteractedRef.current = true;

      if (newValue === '') {
        onChange(null);
      }
      seOptions([]);
      optionsFilterTextRef.current = '__searching__';

      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        setParams(
          (prev) =>
            ({
              ...prev,
              filterText: newValue,
              pageNumber: 1,
            } as Params),
        );
        searchDebounceRef.current = null;
      }, 300);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (_: any, newValue: any) => {
      onChange(newValue);

      if (!newValue) {
        seOptions([]);
        optionsFilterTextRef.current = '__none__';
        setParams(defaultParams);
      } else {
        onSave(newValue);
      }
    },
    [onChange, onSave, defaultParams],
  );

  const handleScrollBottom = useCallback(() => {
    setParams(
      (prev) =>
        ({
          ...prev,
          pageNumber: ((prev as any).pageNumber || 0) + 1,
        } as Params),
    );
  }, []);

  return {
    options,
    loading,
    params,
    handleSearchChange,
    handleChange,
    handleScrollBottom,
  };
}
